import { ArrowLeft, Phone, MapPin, Package, Clock, DollarSign, Bike, PersonStanding, MessageCircle } from 'lucide-react';
import { GoogleSdkMap } from '../map/GoogleSdkMap';
import { isValidJobCoords } from '../../utils/jobCoordinates';

interface Job {
  id: string;
  pickupName: string;
  pickupAddress: string;
  pickupPhone?: string;
  pickupLocation?: { lat: number; lng: number };
  pickupLandmark?: string;
  deliveryName: string;
  deliveryAddress: string;
  deliveryLocation?: { lat: number; lng: number };
  deliveryLandmark?: string;
  distance: string;
  estimatedEarnings: string;
  estimatedTime: string;
  packageType: string;
  customerName: string;
  customerPhone: string;
  items?: string[];
  notes?: string;
  type: 'food' | 'parcel' | 'errand';
  fleetType: 'rider' | 'bicycle' | 'leg-fleet';
  /** Server-decided vehicle for this job (BICYCLE / EBIKE / MOTORCYCLE) */
  vehicleType?: string;
  /** Authoritative delivery fee stored on the order (₦) */
  deliveryFee?: number;
}

interface JobDetailScreenProps {
  job: Job;
  onBack: () => void;
  onAccept: () => void;
  onReject: () => void;
  onOpenChat?: () => void;
}

export function JobDetailScreen({ job, onBack, onAccept, onReject, onOpenChat }: JobDetailScreenProps) {
  const hasMapCoords =
    isValidJobCoords(job.pickupLocation) && isValidJobCoords(job.deliveryLocation);

  const getFleetTypeInfo = (fleetType: string) => {
    const configs = {
      'rider': { label: 'Rider (Motorcycle)', icon: Bike, bgColor: 'bg-[#E0001B]/10', textColor: 'text-[#E0001B]' },
      'bicycle': { label: 'Bicycle', icon: Bike, bgColor: 'bg-[#0066CC]/10', textColor: 'text-[#0066CC]' },
      'leg-fleet': { label: 'Leg Fleet (Walking)', icon: PersonStanding, bgColor: 'bg-[#00B277]/10', textColor: 'text-[#00B277]' }
    };
    return configs[fleetType as keyof typeof configs] || configs.rider;
  };

  // The order's server-decided vehicle wins; fall back to the rider's fleet.
  const jobVehicle = String(job.vehicleType || '').toUpperCase();
  const jobFleetMap: Record<string, string> = {
    MOTORCYCLE: 'rider',
    BICYCLE: 'bicycle',
    EBIKE: 'bicycle',
  };
  const fleetInfo = getFleetTypeInfo(jobVehicle ? jobFleetMap[jobVehicle] || 'rider' : job.fleetType);
  const FleetIcon = fleetInfo.icon;

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[18px] text-[#111111]">Job Details</h1>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${fleetInfo.bgColor}`}>
              <FleetIcon className={`w-3 h-3 ${fleetInfo.textColor}`} />
              <span className={`text-[11px] font-medium ${fleetInfo.textColor}`}>{fleetInfo.label}</span>
            </div>
          </div>
          <p className="text-[12px] text-[#6B6B6B]">Order #{job.id}</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Map Strip */}
        <div className="h-48">
          {hasMapCoords ? (
            <GoogleSdkMap
              pickupLocation={{
                ...job.pickupLocation!,
                address: job.pickupAddress,
              }}
              deliveryLocation={{
                ...job.deliveryLocation!,
                address: job.deliveryAddress,
              }}
              showRoute
              compact
              zoom={13}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center bg-gray-100 px-4 text-center gap-2">
              <MapPin className="w-6 h-6 text-[#E0001B]" />
              <p className="text-sm text-gray-600">
                Waiting for pickup and delivery coordinates in-app.
              </p>
            </div>
          )}
        </div>

        {!hasMapCoords && (
          <div className="mx-4 mt-3 rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
            Navigation will not work until this order has valid pickup and delivery locations.
          </div>
        )}

        <div className="p-4 space-y-6">
          {/* Earnings Highlight */}
          <div className="bg-[#00B277]/10 rounded-[12px] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#00B277] flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-[12px] text-[#6B6B6B]">YOUR PAYOUT</p>
                <p className="text-[22px] text-[#00B277]">{job.estimatedEarnings}</p>
                {typeof job.deliveryFee === 'number' && job.deliveryFee > 0 && (
                  <p className="text-[12px] text-[#6B6B6B]">
                    Delivery fee ₦{job.deliveryFee.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-[#6B6B6B] mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-[14px]">{job.estimatedTime}</span>
              </div>
              <div className="flex items-center gap-1 text-[#6B6B6B]">
                <MapPin className="w-4 h-4" />
                <span className="text-[14px]">{job.distance}</span>
              </div>
              <p className="text-[11px] text-[#6B6B6B] mt-1">Estimated trip distance</p>
            </div>
          </div>

          {/* Pickup Contact */}
          <div className="bg-white rounded-[12px] border border-gray-200 p-4">
            <p className="text-[12px] text-[#6B6B6B] mb-3">PICKUP CONTACT</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[16px] text-[#111111]">{job.pickupName}</p>
                <p className="text-[14px] text-[#6B6B6B]">
                  {job.pickupPhone || 'Phone not provided'}
                </p>
              </div>
              {job.pickupPhone ? (
                <a
                  href={`tel:${job.pickupPhone}`}
                  className="w-12 h-12 rounded-full bg-[#E0001B]/10 flex items-center justify-center hover:bg-[#E0001B]/20 transition-colors"
                  aria-label="Call pickup contact"
                >
                  <Phone className="w-5 h-5 text-[#E0001B]" />
                </a>
              ) : null}
            </div>
          </div>

          {/* Pickup Location */}
          <div className="bg-white rounded-[12px] border border-gray-200 p-4">
            <p className="text-[12px] text-[#6B6B6B] mb-3">PICKUP LOCATION</p>
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E0001B]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#E0001B]" />
              </div>
              <div>
                <p className="text-[16px] text-[#111111] mb-1">{job.pickupName}</p>
                <p className="text-[14px] text-[#6B6B6B]">{job.pickupAddress}</p>
                {job.pickupLandmark && (
                  <p className="text-[12px] text-[#6B6B6B] mt-1">
                    📍 Landmark: {job.pickupLandmark}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Contact */}
          <div className="bg-white rounded-[12px] border border-gray-200 p-4">
            <p className="text-[12px] text-[#6B6B6B] mb-3">DELIVERY CONTACT</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[16px] text-[#111111]">{job.customerName}</p>
                <p className="text-[14px] text-[#6B6B6B]">
                  {job.customerPhone || 'Phone not provided'}
                </p>
              </div>
              <div className="flex items-center gap-2">
              {job.customerPhone ? (
                <a
                  href={`tel:${job.customerPhone}`}
                  className="w-12 h-12 rounded-full bg-[#00B277]/10 flex items-center justify-center hover:bg-[#00B277]/20 transition-colors"
                  aria-label="Call delivery contact"
                >
                  <Phone className="w-5 h-5 text-[#00B277]" />
                </a>
              ) : null}
              {onOpenChat ? (
                <button
                  type="button"
                  onClick={onOpenChat}
                  className="w-12 h-12 rounded-full bg-[#111111]/10 flex items-center justify-center hover:bg-[#111111]/20 transition-colors"
                  aria-label="Chat with customer"
                >
                  <MessageCircle className="w-5 h-5 text-[#111111]" />
                </button>
              ) : null}
              </div>
            </div>
          </div>

          {/* Delivery Location */}
          <div className="bg-white rounded-[12px] border border-gray-200 p-4">
            <p className="text-[12px] text-[#6B6B6B] mb-3">DELIVERY LOCATION</p>
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#00B277]" />
              </div>
              <div>
                <p className="text-[16px] text-[#111111] mb-1">{job.deliveryName}</p>
                <p className="text-[14px] text-[#6B6B6B]">{job.deliveryAddress}</p>
                {job.deliveryLandmark && (
                  <p className="text-[12px] text-[#6B6B6B] mt-1">
                    📍 Landmark: {job.deliveryLandmark}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Package Info */}
          <div className="bg-white rounded-[12px] border border-gray-200 p-4">
            <p className="text-[12px] text-[#6B6B6B] mb-3">PACKAGE DETAILS</p>
            <div className="flex items-center gap-3 mb-3">
              <Package className="w-5 h-5 text-[#E0001B]" />
              <p className="text-[16px] text-[#111111]">{job.packageType}</p>
            </div>
            {job.items && job.items.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-[12px] text-[#6B6B6B] mb-2">ITEMS</p>
                <ul className="space-y-1">
                  {job.items.map((item, index) => (
                    <li key={index} className="text-[14px] text-[#111111] flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#6B6B6B]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Special Instructions */}
          {job.notes && (
            <div className="bg-amber-50 rounded-[12px] border border-amber-200 p-4">
              <p className="text-[12px] text-amber-900 mb-2">⚠️ SPECIAL INSTRUCTIONS</p>
              <p className="text-[14px] text-amber-900">{job.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-3">
          <button
            onClick={onReject}
            className="flex-1 h-12 rounded-[12px] border border-[#E0001B] text-[#E0001B] hover:bg-[#E0001B]/5 transition-colors"
          >
            Reject
          </button>
          <button
            onClick={onAccept}
            disabled={!hasMapCoords}
            className="flex-1 h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasMapCoords ? 'Accept Job' : 'Map Unavailable'}
          </button>
        </div>
      </div>
    </div>
  );
}